// SNIGJ - Digital Signature Page for Documents
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import DigitalSignature from '../components/documents/DigitalSignature';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  FileSignature, ArrowLeft, FileText, User, Calendar, CheckCircle,
  Shield, Download, Eye, Clock, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const DocumentSigningPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useAuth();
  
  const [document, setDocument] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    if (documentId) {
      loadDocument();
      loadSignatures();
    }
  }, [documentId]);

  const loadDocument = async () => {
    try {
      const response = await api.get(`/documents/${documentId}`);
      setDocument(response.data);
    } catch (error) {
      console.error('Failed to load document:', error);
      toast.error(language === 'fr' ? 'Document non trouvé' : 'Document not found');
    } finally {
      setLoading(false);
    }
  };

  const loadSignatures = async () => {
    try {
      const response = await api.get(`/documents/${documentId}/signatures`);
      setSignatures(response.data.signatures || []);
      
      // Check if current user already signed
      const userSigned = response.data.signatures?.some(s => s.signer_id === user?.user_id);
      setSigned(userSigned);
    } catch (error) {
      console.error('Failed to load signatures:', error);
    }
  };

  const handleSignatureComplete = (result) => {
    setSigned(true);
    loadSignatures();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="h-8 w-64 skeleton" />
          <div className="grid grid-cols-2 gap-6">
            <div className="h-96 skeleton" />
            <div className="h-96 skeleton" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl" data-testid="document-signing-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-3">
              <FileSignature className="w-7 h-7 text-primary" />
              {language === 'fr' ? 'Signature de Document' : 'Document Signing'}
            </h1>
            <p className="text-muted-foreground">
              {document?.title || documentId}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Document Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {language === 'fr' ? 'Détails du Document' : 'Document Details'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {document ? (
                  <>
                    <div className="flex justify-between items-start">
                      <span className="text-muted-foreground">{language === 'fr' ? 'Titre' : 'Title'}:</span>
                      <span className="font-medium text-right">{document.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{language === 'fr' ? 'Type' : 'Type'}:</span>
                      <Badge variant="outline">{document.document_type}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{language === 'fr' ? 'Statut' : 'Status'}:</span>
                      <Badge className={document.status === 'SIGNED' ? 'bg-green-500' : 'bg-yellow-500'}>
                        {document.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{language === 'fr' ? 'Créé le' : 'Created'}:</span>
                      <span>{new Date(document.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                    </div>
                    {document.case_id && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Dossier' : 'Case'}:</span>
                        <span className="font-mono text-sm">{document.case_id}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="w-12 h-12 mx-auto mb-2" />
                    {language === 'fr' ? 'Document non trouvé' : 'Document not found'}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Existing Signatures */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    {language === 'fr' ? 'Signatures' : 'Signatures'}
                  </div>
                  <Badge variant="secondary">{signatures.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {signatures.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    {language === 'fr' ? 'Aucune signature' : 'No signatures yet'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {signatures.map((sig, index) => (
                      <div key={sig.signature_id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="font-medium">{sig.signer_name}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">{sig.signer_role}</Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {new Date(sig.signed_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}
                        </div>
                        <p className="text-xs font-mono mt-1 text-muted-foreground">
                          ID: {sig.signature_id}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Signature Component */}
          <div>
            {signed ? (
              <Card className="bg-green-50 dark:bg-green-900/20 border-green-200">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-green-800 dark:text-green-200 mb-2">
                    {language === 'fr' ? 'Vous avez déjà signé ce document' : 'You have already signed this document'}
                  </h3>
                  <p className="text-green-700 dark:text-green-300">
                    {language === 'fr' 
                      ? 'Votre signature a été enregistrée et vérifiée.'
                      : 'Your signature has been recorded and verified.'}
                  </p>
                </CardContent>
              </Card>
            ) : document ? (
              <DigitalSignature
                documentId={documentId}
                documentTitle={document.title}
                onSignatureComplete={handleSignatureComplete}
                requirePin={false}
              />
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {language === 'fr' ? 'Impossible de charger le document' : 'Unable to load document'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DocumentSigningPage;
